/** Quotes & invoices hooks. API data only when authenticated (errors → isError + empty). */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ACCOUNTING_QUERY_PREFIX,
  REPORTS_QUERY_PREFIX,
  TREASURY_QUERY_PREFIX,
  queryKeys,
} from "@/lib/query-client";
import type { Invoice, Quote } from "@/types";
import { useAuthStore } from "@/features/auth/auth.store";
import { invoicesApi } from "./invoices.api";
import { toInvoice, toQuote, type QuoteInput } from "./invoices.adapter";

const LIST_PARAMS = { limit: 100, sortBy: "createdAt", sortOrder: "desc" as const };

/** Narrow list prefixes (match every params variant) — narrower than the whole ["invoices"] tree. */
const QUOTES_LIST_KEY = ["invoices", "quotes"] as const;
const INVOICES_LIST_KEY = ["invoices", "list"] as const;

export type DataSource = "api" | "mock";

export function useQuotes(): {
  quotes: Quote[];
  isLoading: boolean;
  isError: boolean;
  source: DataSource;
  refetch: () => void;
} {
  const status = useAuthStore((s) => s.status);
  const authed = status === "authenticated";
  const query = useQuery({
    queryKey: queryKeys.quotesList(LIST_PARAMS),
    queryFn: () => invoicesApi.listQuotes(LIST_PARAMS),
    enabled: authed,
  });
  if (query.data)
    return {
      quotes: query.data.data.map(toQuote),
      isLoading: false,
      isError: false,
      source: "api",
      refetch: query.refetch,
    };
  if (query.isError)
    return { quotes: [], isLoading: false, isError: true, source: "api", refetch: query.refetch };
  return {
    quotes: [],
    isLoading: query.isLoading,
    isError: false,
    source: "api",
    refetch: query.refetch,
  };
}

export function useInvoices(): {
  invoices: Invoice[];
  isLoading: boolean;
  isError: boolean;
  source: DataSource;
  refetch: () => void;
} {
  const status = useAuthStore((s) => s.status);
  const authed = status === "authenticated";
  const query = useQuery({
    queryKey: queryKeys.invoicesList(LIST_PARAMS),
    queryFn: () => invoicesApi.listInvoices(LIST_PARAMS),
    enabled: authed,
  });
  if (query.data)
    return {
      invoices: query.data.data.map(toInvoice),
      isLoading: false,
      isError: false,
      source: "api",
      refetch: query.refetch,
    };
  if (query.isError)
    return { invoices: [], isLoading: false, isError: true, source: "api", refetch: query.refetch };
  return {
    invoices: [],
    isLoading: query.isLoading,
    isError: false,
    source: "api",
    refetch: query.refetch,
  };
}

/**
 * Granular, non-blocking invalidators. Quote-only and invoice-only mutations no
 * longer nuke the whole ["invoices"] tree or touch treasury; only payment
 * mutations (which actually post into treasury) invalidate the treasury prefix.
 */
function useInvalidateInvoicesScoped() {
  const qc = useQueryClient();
  return {
    quotes: () => void qc.invalidateQueries({ queryKey: QUOTES_LIST_KEY }),
    invoices: () => void qc.invalidateQueries({ queryKey: INVOICES_LIST_KEY }),
    // quote → invoice conversion affects both lists
    quotesAndInvoices: () => {
      void qc.invalidateQueries({ queryKey: QUOTES_LIST_KEY });
      void qc.invalidateQueries({ queryKey: INVOICES_LIST_KEY });
    },
    // payments move treasury balances AND the accounting overview + dashboard
    // (ADR-001: un paiement = FinancialTransaction + Movement, trois pages).
    invoicesAndTreasury: () => {
      void qc.invalidateQueries({ queryKey: INVOICES_LIST_KEY });
      void qc.invalidateQueries({ queryKey: TREASURY_QUERY_PREFIX });
      void qc.invalidateQueries({ queryKey: ACCOUNTING_QUERY_PREFIX });
      void qc.invalidateQueries({ queryKey: REPORTS_QUERY_PREFIX });
    },
  };
}

export function useCreateQuote() {
  const invalidate = useInvalidateInvoicesScoped();
  return useMutation({
    mutationFn: async (input: QuoteInput) => {
      return invoicesApi.createQuote({ ...input });
    },
    onSuccess: () => invalidate.quotes(),
  });
}

export function useCreateInvoice() {
  const invalidate = useInvalidateInvoicesScoped();
  return useMutation({
    mutationFn: async (input: QuoteInput & { dueDate?: string }) => {
      const { validityDays: _v, ...rest } = input;
      return invoicesApi.createInvoice({ ...rest });
    },
    onSuccess: () => invalidate.invoices(),
  });
}

export function useConvertQuote() {
  const invalidate = useInvalidateInvoicesScoped();
  return useMutation({
    mutationFn: async (id: string) => {
      return invoicesApi.convertQuote(id);
    },
    // conversion removes/updates a quote and creates an invoice
    onSuccess: () => invalidate.quotesAndInvoices(),
  });
}

export function useSetQuoteStatus() {
  const invalidate = useInvalidateInvoicesScoped();
  return useMutation({
    mutationFn: async ({
      id,
      status,
    }: {
      id: string;
      status: "SENT" | "ACCEPTED" | "REJECTED";
    }) => {
      return invoicesApi.setQuoteStatus(id, status);
    },
    onSuccess: () => invalidate.quotes(),
  });
}

export function useRecordInvoicePayment() {
  const invalidate = useInvalidateInvoicesScoped();
  return useMutation({
    mutationFn: async ({
      id,
      input,
    }: {
      id: string;
      input: { amount: number; accountId: string; method: string; note?: string };
    }) => {
      return invoicesApi.recordPayment(id, { ...input });
    },
    // payment posts into treasury — refresh invoices + treasury balances
    onSuccess: () => invalidate.invoicesAndTreasury(),
  });
}

export function useArchiveDoc() {
  const invalidate = useInvalidateInvoicesScoped();
  return useMutation({
    mutationFn: async ({ id, kind }: { id: string; kind: "quote" | "invoice" }) => {
      return kind === "quote" ? invoicesApi.archiveQuote(id) : invoicesApi.archiveInvoice(id);
    },
    onSuccess: (_data, { kind }) =>
      kind === "quote" ? invalidate.quotes() : invalidate.invoices(),
  });
}
